<template>
  <div
    :class="{
      'no-wrap': dense && tiny,
      'q-col-gutter-sm': dense && tiny,
      'q-col-gutter-md': !dense || !tiny
    }"
    class="row"
  >
    <universal-tooltip
      :show-tooltip="sessionProgram.program?.timerAutoStart"
      :text="$t('programs.dataCollection.frequencyTimer.syncTooltipText')"
    >
      <frequency-timer
        v-if="hasDataObject && !isPinned"
        ref="timer"
        v-model="data.duration"
        :paused="data.isTimerPaused"
        :duration-offset="timerOffsetTimestamp"
        :session-paused="sessionPaused"
        :session-duration-offset="sessionDurationOffset"
        :disable="disable || sessionProgram.program?.timerAutoStart"
        :dense="dense"
        :session-program="sessionProgram.id"
        @pause="onPauseFrequencyTimer"
        @start="onResumeFrequencyTimer"
        @update="onUpdateFrequencyTimer"
      />
    </universal-tooltip>
    <template
      v-for="target in sortedTargets"
      :key="target.id"
    >
      <target-data
        ref="targets"
        :session-program="sessionProgram"
        :target="target"
        :data="data"
        :active-prompt-levels="activePromptLevels"
        :workflow-statuses="workflowStatuses"
        :dense="dense"
        :tiny="tiny"
        :disable="disable || isPaused"
        :dimmed="isPaused"
        @record="onRecord"
        @record-program-data="onRecordProgramData"
      />
    </template>
  </div>
</template>
<script>
import FrequencyTimer from 'components/Programs/DataCollection/FrequencyTimer';
import TargetData from 'components/Programs/DataCollection/TargetData';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { Timestamp } from 'firebase/firestore';
import { chain, get, isObject } from 'lodash';
import DataCollectionProgram from 'src/mixins/DataCollectionProgram';

export default {
  name: 'FrequencyProgram',

  components: {
    UniversalTooltip,
    FrequencyTimer,
    TargetData,
  },

  mixins: [DataCollectionProgram],

  emits: ['record-program-data'],

  data() {
    return {
    };
  },

  computed: {

    hasDataObject() {
      return isObject(this.data);
    },

    isPinned() {
      return get(this.sessionProgram, 'pinned', false);
    },

    timerOffsetTimestamp() {
      const timerOffsetTimestamp = get(this.data, 'timerOffsetTimestamp');
      if (chain(timerOffsetTimestamp).get('toDate').isFunction().value()) {
        return timerOffsetTimestamp.toDate();
      }
      return null;
    }, // timerOffsetTimestamp()

    isPaused() {
      return (!this.isPinned) && get(this.data, 'isTimerPaused', false);
    },

  },

  methods: {

    finishSession() {
      if (this.isPinned) {
        return {
          durationFromSession: true,
        };
      }
      if (chain(this.$refs).get(['timer', 'finishSession']).isFunction().value()) {
        const dataObj = this.$refs.timer.finishSession();
        return {
          sessionData: {
            ...dataObj,
            timerOffsetTimestamp: null,
          },
        };
      }
    },

    onUpdateFrequencyTimer(duration, timerOffsetTimestampDate = null) {
      const timerOffsetTimestamp = (timerOffsetTimestampDate !== null)
        ? Timestamp.fromDate(timerOffsetTimestampDate)
        : null;
      const dataObj = {
        duration,
        timerOffsetTimestamp,
      };
      this.$emit('record-program-data', this.sessionProgram, dataObj);
    }, // onUpdateFrequencyTimer()

    onPauseFrequencyTimer(duration) {
      if (this.disable) {
        return;
      }

      const dataObj = {
        duration,
        timerOffsetTimestamp: null,
        isTimerPaused: true,
      };

      this.$emit('record-program-data', this.sessionProgram, dataObj);
    }, // onPauseFrequencyTimer()

    onResumeFrequencyTimer(duration) {
      const dataObj = {
        duration,
        timerOffsetTimestamp: Timestamp.now(),
        isTimerPaused: false,
      };
      if (chain(this.data).get('data', []).isEmpty().value()) {
        dataObj.data = this.initEmptyTargetData();
      }
      this.$emit('record-program-data', this.sessionProgram, dataObj);
    }, // onResumeFrequencyTimer()

    initEmptyTargetData() {
      const sessionProgramSelectedTargets = get(
        this.sessionProgram,
        'selectedTargets',
        false,
      );
      return chain(this.sessionProgram)
        .get(['program', 'targets'])
        // Reject statuses that are not used in session.
        // If no targets were found it indicates that the program has custom status, so we should always include it
        // as custom statuses cannot be hidden
        .filter(
          (target) => {
            return sessionProgramSelectedTargets
              ? get(sessionProgramSelectedTargets, target.status, true)
              : false;
          },
        )
        .map((target) => ({
          target: target.id,
        }))
        .value();
    }, // initEmptyTargetData()
  },
};
</script>
